import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { extractError } from "@/lib/api";
import { LOGO_HEADER, LOGO_FOOTER, ADDRESS_LINE, APP_NAME, APP_SUBTITLE } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import {
  Loader2, LogIn, Headset, FolderKanban, CalendarClock, ShieldCheck,
  AlertCircle, MailCheck, RotateCcw, ArrowLeft, CheckCircle2, KeyRound,
} from "lucide-react";

const FEATURES = [
  { icon: Headset, title: "Modalità Centralino", desc: "Registra il primo contatto durante la telefonata." },
  { icon: FolderKanban, title: "Pratiche & Diario", desc: "Gestisci casi, assegnazioni e cronologia." },
  { icon: CalendarClock, title: "Agenda", desc: "Appuntamenti, corsi e attività della Rete." },
];

const RESEND_COOLDOWN = 30;

const Login = () => {
  const { login, verify2fa, resend2fa, verifyBackupCode, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // step 1: credenziali
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // step 2: verifica in due passaggi
  const [step, setStep] = useState("credentials"); // "credentials" | "otp"
  const [twofaMode, setTwofaMode] = useState("email"); // "email" | "backup"
  const [challengeToken, setChallengeToken] = useState("");
  const [emailMasked, setEmailMasked] = useState("");
  const [code, setCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpInfo, setOtpInfo] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Se già autenticato, salta il login
  useEffect(() => {
    if (user) navigate("/app", { replace: true });
  }, [user, navigate]);

  // Countdown per il reinvio del codice
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.twofaRequired) {
        setChallengeToken(res.challengeToken);
        setEmailMasked(res.emailMasked || "");
        setCode("");
        setBackupCode("");
        setTwofaMode("email");
        setOtpError("");
        setOtpInfo("");
        setStep("otp");
        setCooldown(RESEND_COOLDOWN);
      } else {
        const dest = location.state?.from?.pathname || "/app";
        navigate(dest, { replace: true });
      }
    } catch (err) {
      setError(extractError(err, "Credenziali non valide. Riprova."));
    } finally {
      setLoading(false);
    }
  };

  const doVerify = async (value) => {
    const c = (value ?? code).replace(/\D/g, "");
    if (c.length < 6 || verifying) return;
    setOtpError("");
    setVerifying(true);
    try {
      await verify2fa(challengeToken, c);
      const dest = location.state?.from?.pathname || "/app";
      navigate(dest, { replace: true });
    } catch (err) {
      setOtpError(extractError(err, "Codice non valido. Riprova."));
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  const onCodeChange = (val) => {
    const digits = (val || "").replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    if (otpError) setOtpError("");
    if (digits.length === 6) doVerify(digits);
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setOtpError("");
    setOtpInfo("");
    try {
      const data = await resend2fa(challengeToken);
      if (data?.email_masked) setEmailMasked(data.email_masked);
      setCode("");
      setCooldown(RESEND_COOLDOWN);
      setOtpInfo("Ti abbiamo inviato un nuovo codice via email.");
    } catch (err) {
      setOtpError(extractError(err, "Impossibile reinviare il codice. Riprova tra poco."));
    } finally {
      setResending(false);
    }
  };

  const handleBack = () => {
    setStep("credentials");
    setTwofaMode("email");
    setChallengeToken("");
    setCode("");
    setBackupCode("");
    setOtpError("");
    setOtpInfo("");
    setPassword("");
  };

  const doVerifyBackup = async () => {
    const c = backupCode.trim();
    if (c.length < 6 || verifying) return;
    setOtpError("");
    setVerifying(true);
    try {
      await verifyBackupCode(challengeToken, c);
      const dest = location.state?.from?.pathname || "/app";
      navigate(dest, { replace: true });
    } catch (err) {
      setOtpError(extractError(err, "Codice di riserva non valido."));
    } finally {
      setVerifying(false);
    }
  };

  const switchToBackup = () => {
    setTwofaMode("backup");
    setOtpError("");
    setOtpInfo("");
    setCode("");
    setBackupCode("");
  };

  const switchToEmail = () => {
    setTwofaMode("email");
    setOtpError("");
    setOtpInfo("");
    setBackupCode("");
    setCode("");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Sinistra: pannello brand / contesto operativo interno */}
      <div className="hidden lg:flex flex-col justify-between ras-hero-bg p-10 border-r">
        <div className="flex items-center gap-3">
          <img src={LOGO_HEADER} alt="Azioni Solidali in Rete" className="h-14 w-14 rounded-full object-contain" />
          <div>
            <div className="font-display font-bold text-xl text-[color:var(--brand-blue-700)]">{APP_NAME}</div>
            <div className="text-sm text-muted-foreground">{APP_SUBTITLE}</div>
          </div>
        </div>

        <div className="max-w-md">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur border px-3 py-1 text-xs font-medium text-[color:var(--brand-orange-600)] mb-5">
            <ShieldCheck className="h-4 w-4" /> Area riservata · uso interno
          </span>
          <h2 className="font-display text-3xl font-semibold text-slate-900 leading-tight">
            La memoria organizzativa e relazionale della Rete.
          </h2>
          <p className="mt-3 text-slate-600">
            La persona al centro, i servizi attorno. Accedi per registrare i contatti e gestire le pratiche.
          </p>

          <div className="mt-8 space-y-3">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex items-start gap-3 rounded-xl border bg-white/70 backdrop-blur px-4 py-3">
                <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[color:var(--brand-orange-100)] text-[color:var(--brand-orange-600)]">
                  <f.icon className="h-5 w-5" />
                </span>
                <div>
                  <div className="font-medium text-sm text-slate-900">{f.title}</div>
                  <div className="text-xs text-slate-600">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <img src={LOGO_FOOTER} alt="Cantieri Educativi" className="h-9 object-contain" />
          <p className="text-[11px] text-muted-foreground">{ADDRESS_LINE}</p>
        </div>
      </div>

      {/* Destra: form di accesso */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <img src={LOGO_HEADER} alt="Azioni Solidali" className="h-12 w-12 rounded-full object-contain" />
            <div>
              <div className="font-display font-bold text-[color:var(--brand-blue-700)]">{APP_NAME}</div>
              <div className="text-xs text-muted-foreground">{APP_SUBTITLE}</div>
            </div>
          </div>

          <div className="rounded-[var(--radius)] border bg-card p-6 sm:p-8 shadow-sm">
            {step === "credentials" ? (
              <>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-blue-100)] px-2.5 py-0.5 text-[11px] font-medium text-[color:var(--brand-blue-700)]">
                  <ShieldCheck className="h-3.5 w-3.5" /> Accesso riservato
                </span>
                <h1 className="font-display text-2xl font-semibold mt-3">Accedi all'area riservata</h1>
                <p className="text-sm text-muted-foreground mt-1">Inserisci le tue credenziali per continuare.</p>

                {error && (
                  <Alert variant="destructive" className="mt-4" data-testid="login-error-alert" aria-live="assertive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={submit} className="mt-5 space-y-4" noValidate>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" autoComplete="username" inputMode="email" value={email}
                      onChange={(e) => setEmail(e.target.value)} required className="h-11 mt-1"
                      aria-invalid={!!error} data-testid="login-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input id="password" type="password" autoComplete="current-password" value={password}
                      onChange={(e) => setPassword(e.target.value)} required className="h-11 mt-1"
                      aria-invalid={!!error} data-testid="login-password-input" />
                  </div>
                  <Button type="submit" disabled={loading}
                    className="w-full h-11 min-h-[44px] bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white transition-[background-color,box-shadow,transform] duration-200 hover:-translate-y-px"
                    data-testid="login-submit-button">
                    {loading ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Accesso…</> : <><LogIn className="h-5 w-5 mr-2" /> Entra</>}
                  </Button>
                </form>
              </>
            ) : (
              <div data-testid="login-2fa-step">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-blue-100)] px-2.5 py-0.5 text-[11px] font-medium text-[color:var(--brand-blue-700)]">
                  <ShieldCheck className="h-3.5 w-3.5" /> Verifica in due passaggi
                </span>
                <h1 className="font-display text-2xl font-semibold mt-3">
                  {twofaMode === "email" ? "Inserisci il codice" : "Codice di riserva"}
                </h1>
                {twofaMode === "email" ? (
                  <p className="text-sm text-muted-foreground mt-1">
                    Abbiamo inviato un codice di verifica a 6 cifre
                    {emailMasked ? <> a <span className="font-medium text-foreground">{emailMasked}</span></> : " alla tua email"}.
                    Controlla la posta e inseriscilo qui sotto.
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">
                    Non ricevi l'email? Inserisci uno dei codici di riserva monouso che hai salvato in precedenza.
                  </p>
                )}

                {otpError && (
                  <Alert variant="destructive" className="mt-4" data-testid="login-2fa-error-alert" aria-live="assertive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{otpError}</AlertDescription>
                  </Alert>
                )}
                {otpInfo && !otpError && (
                  <Alert className="mt-4 border-[color:var(--brand-blue-100)] bg-[color:var(--brand-blue-100)] text-[color:var(--brand-blue-700)]" aria-live="polite">
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>{otpInfo}</AlertDescription>
                  </Alert>
                )}

                {twofaMode === "email" ? (
                  <div className="mt-6 flex flex-col items-center gap-4">
                    <span className="grid h-12 w-12 place-items-center rounded-full bg-[color:var(--brand-orange-100)] text-[color:var(--brand-orange-600)]">
                      <MailCheck className="h-6 w-6" />
                    </span>
                    <InputOTP
                      maxLength={6}
                      value={code}
                      onChange={onCodeChange}
                      disabled={verifying}
                      inputMode="numeric"
                      containerClassName="justify-center"
                      data-testid="login-2fa-code-input"
                      aria-label="Codice di verifica a 6 cifre"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} className="h-12 w-11 text-lg" />
                        <InputOTPSlot index={1} className="h-12 w-11 text-lg" />
                        <InputOTPSlot index={2} className="h-12 w-11 text-lg" />
                        <InputOTPSlot index={3} className="h-12 w-11 text-lg" />
                        <InputOTPSlot index={4} className="h-12 w-11 text-lg" />
                        <InputOTPSlot index={5} className="h-12 w-11 text-lg" />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                ) : (
                  <div className="mt-6">
                    <div className="flex justify-center mb-4">
                      <span className="grid h-12 w-12 place-items-center rounded-full bg-[color:var(--brand-orange-100)] text-[color:var(--brand-orange-600)]">
                        <KeyRound className="h-6 w-6" />
                      </span>
                    </div>
                    <Label htmlFor="backup-code">Codice di riserva</Label>
                    <Input
                      id="backup-code"
                      value={backupCode}
                      onChange={(e) => { setBackupCode(e.target.value.toUpperCase()); if (otpError) setOtpError(""); }}
                      onKeyDown={(e) => { if (e.key === "Enter") doVerifyBackup(); }}
                      placeholder="XXXXX-XXXXX"
                      autoComplete="one-time-code"
                      disabled={verifying}
                      className="h-12 mt-1 text-center font-mono tracking-[0.2em] text-lg uppercase"
                      data-testid="login-2fa-backup-input"
                      aria-label="Codice di riserva"
                    />
                  </div>
                )}

                <Button
                  type="button"
                  onClick={twofaMode === "email" ? () => doVerify() : doVerifyBackup}
                  disabled={verifying || (twofaMode === "email" ? code.length < 6 : backupCode.trim().length < 6)}
                  className="w-full h-11 min-h-[44px] mt-6 bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white transition-[background-color,box-shadow,transform] duration-200 hover:-translate-y-px"
                  data-testid="login-2fa-verify-button">
                  {verifying ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Verifica…</> : <><ShieldCheck className="h-5 w-5 mr-2" /> Verifica e accedi</>}
                </Button>

                <div className="mt-4 flex items-center justify-between gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleBack}
                    disabled={verifying}
                    className="h-10 px-2 text-muted-foreground hover:text-foreground"
                    data-testid="login-2fa-back-button">
                    <ArrowLeft className="h-4 w-4 mr-1.5" /> Indietro
                  </Button>
                  {twofaMode === "email" && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleResend}
                      disabled={cooldown > 0 || resending || verifying}
                      className="h-10 px-2 text-[color:var(--brand-blue-700)] hover:text-[color:var(--brand-blue-700)]"
                      data-testid="login-2fa-resend-button">
                      {resending
                        ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Invio…</>
                        : <><RotateCcw className="h-4 w-4 mr-1.5" /> {cooldown > 0 ? `Reinvia tra ${cooldown}s` : "Reinvia codice"}</>}
                    </Button>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t text-center">
                  {twofaMode === "email" ? (
                    <button
                      type="button"
                      onClick={switchToBackup}
                      disabled={verifying}
                      className="text-xs text-[color:var(--brand-blue-700)] hover:underline inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="login-2fa-use-backup-button">
                      <KeyRound className="h-3.5 w-3.5" /> Non ricevi l'email? Usa un codice di riserva
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={switchToEmail}
                      disabled={verifying}
                      className="text-xs text-[color:var(--brand-blue-700)] hover:underline inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="login-2fa-use-email-button">
                      <MailCheck className="h-3.5 w-3.5" /> Usa il codice ricevuto via email
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground text-center mt-5 flex items-center justify-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" /> Connessione protetta · dati trattati secondo il GDPR
          </p>

          {/* Footer brand Cantieri Educativi: solo mobile/tablet (su desktop è nel pannello sinistro) */}
          <div className="lg:hidden mt-6 pt-5 border-t flex flex-col items-center gap-2 text-center pb-[calc(1rem_+_env(safe-area-inset-bottom))]" data-testid="login-mobile-footer">
            <img src={LOGO_FOOTER} alt="Cantieri Educativi" className="h-11 object-contain" data-testid="login-footer-logo" />
            <p className="text-[11px] text-muted-foreground max-w-xs">{ADDRESS_LINE}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
